import { useCallback, useMemo, useState } from 'react';

import type { GroupSubscriptionData } from '@shared/dream-components';

import type { Subscription } from '~/interfaces/subscriptions';
import {
  type GeneralScenarioKey,
  getScenarioGroupSubscriptions,
  getScenarioSubscription,
} from '~/routes/subscribe/$subscriberId/_components/v2/hooks/generalScenarios';

import { DevScenarioContext, type DevScenarioContextType } from './DevScenarioContext';

/**
 * Owns the dev-only General-tab scenario override (BEE-18984). Mounted high so
 * the General view reads the scenario-adjusted subscription and
 * `GroupSubscriptionsProvider` applies the same scenario to its group list via
 * `applyGroupScenario`. A no-op passthrough in production where `scenario` stays `'live'`.
 */
export const DevScenarioProvider = ({
  subscription,
  children,
}: {
  subscription: Subscription;
  children: React.ReactNode;
}) => {
  const [scenario, setScenario] = useState<GeneralScenarioKey>('live');

  const effectiveSubscription = useMemo(
    () => getScenarioSubscription(scenario, subscription),
    [scenario, subscription],
  );

  const applyGroupScenario = useCallback(
    (groups: GroupSubscriptionData[]) => getScenarioGroupSubscriptions(scenario, groups),
    [scenario],
  );

  const value = useMemo<DevScenarioContextType>(
    () => ({ scenario, setScenario, effectiveSubscription, applyGroupScenario }),
    [scenario, effectiveSubscription, applyGroupScenario],
  );

  return <DevScenarioContext.Provider value={value}>{children}</DevScenarioContext.Provider>;
};
